import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import HomeIcon from '@mui/icons-material/Home';
import SchoolIcon from '@mui/icons-material/School';
import { Box, Collapse, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { CourseCategory } from '@/lib/api';
import { authSelectors } from '@/state/auth';

export const DrawerNav = () => {
    const { t } = useTranslation();
    const isOrgAdmin = useSelector(authSelectors.isOrgAdmin);
    const isSuperuser = useSelector(authSelectors.isSuperuser);

    const [coursesOpen, setCoursesOpen] = useState(false);
    const [reportingOpen, setReportingOpen] = useState(false);
    const [adminOpen, setAdminOpen] = useState(false);
    const [siteAdminOpen, setSiteAdminOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const isActive = (path: string) => {
        const [pathname, search] = path.split('?');
        if (pathname !== location.pathname) {
            return false;
        }
        if (!search) {
            return !location.search;
        }
        return location.search === `?${search}`;
    };

    return (
        <Box sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 67px)' }}>
            <List disablePadding>
                <ListItem disablePadding>
                    <ListItemButton selected={isActive('/')} onClick={() => navigate('/')}>
                        <ListItemIcon>
                            <HomeIcon />
                        </ListItemIcon>
                        <ListItemText primary={t('nav.menu.home', 'Home')} />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton onClick={() => setCoursesOpen(!coursesOpen)}>
                        <ListItemIcon>
                            <SchoolIcon />
                        </ListItemIcon>
                        <ListItemText>{t('nav.menu.courses', 'Courses')}</ListItemText>
                        {coursesOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                </ListItem>

                <Collapse in={coursesOpen} timeout="auto" unmountOnExit>
                    <ListItem disablePadding>
                        <ListItemButton
                            selected={isActive('/courses?enrolled=true')}
                            onClick={() => navigate('/courses?enrolled=true')}
                        >
                            <ListItemText primary={t('nav.menu.my_courses', 'My Courses')} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton selected={isActive('/courses')} onClick={() => navigate('/courses')}>
                            <ListItemText primary={t('nav.menu.all_courses', 'All Courses')} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton
                            selected={isActive(`/courses?category=${CourseCategory.business_planning}`)}
                            onClick={() => navigate(`/courses?category=${CourseCategory.business_planning}`)}
                        >
                            <ListItemText
                                primary={t(`nav.menu.course_category.business_planning`, 'Business Planning')}
                            />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton
                            selected={isActive(`/courses?category=${CourseCategory.client_management}`)}
                            onClick={() => navigate(`/courses?category=${CourseCategory.client_management}`)}
                        >
                            <ListItemText
                                primary={t(`nav.menu.course_category.client_management`, 'Client Management')}
                            />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton
                            selected={isActive(`/courses?category=${CourseCategory.communication}`)}
                            onClick={() => navigate(`/courses?category=${CourseCategory.communication}`)}
                        >
                            <ListItemText primary={t(`nav.menu.course_category.communication`, 'Communication')} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton
                            selected={isActive(`/courses?category=${CourseCategory.finance}`)}
                            onClick={() => navigate(`/courses?category=${CourseCategory.finance}`)}
                        >
                            <ListItemText primary={t(`nav.menu.course_category.finance`, 'Finance')} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton
                            selected={isActive(`/courses?category=${CourseCategory.hiring}`)}
                            onClick={() => navigate(`/courses?category=${CourseCategory.hiring}`)}
                        >
                            <ListItemText primary={t(`nav.menu.course_category.hiring`, 'Hiring')} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton
                            selected={isActive(`/courses?category=${CourseCategory.management}`)}
                            onClick={() => navigate(`/courses?category=${CourseCategory.management}`)}
                        >
                            <ListItemText primary={t(`nav.menu.course_category.management`, 'Management')} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton
                            selected={isActive(`/courses?category=${CourseCategory.marketing}`)}
                            onClick={() => navigate(`/courses?category=${CourseCategory.marketing}`)}
                        >
                            <ListItemText primary={t(`nav.menu.course_category.marketing`, 'Marketing')} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton
                            selected={isActive(`/courses?category=${CourseCategory.sales}`)}
                            onClick={() => navigate(`/courses?category=${CourseCategory.sales}`)}
                        >
                            <ListItemText primary={t(`nav.menu.course_category.sales`, 'Sales')} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton
                            selected={isActive(`/courses?category=${CourseCategory.software_and_technology}`)}
                            onClick={() => navigate(`/courses?category=${CourseCategory.software_and_technology}`)}
                        >
                            <ListItemText
                                primary={t(
                                    `nav.menu.course_category.software_and_technology`,
                                    'Software and Technology'
                                )}
                            />
                        </ListItemButton>
                    </ListItem>
                </Collapse>

                {isOrgAdmin && (
                    <>
                        <ListItem disablePadding>
                            <ListItemButton onClick={() => setAdminOpen(!adminOpen)}>
                                <ListItemIcon>
                                    <AdminPanelSettings />
                                </ListItemIcon>
                                <ListItemText>{t('nav.menu.administration', 'Administration')}</ListItemText>
                                {coursesOpen ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                        </ListItem>
                        <Collapse in={adminOpen} timeout="auto" unmountOnExit>
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={isActive('/admin/members')}
                                    onClick={() => navigate('/admin/members')}
                                >
                                    <ListItemText primary={t('nav.menu.admin_members', 'Members')} />
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={isActive('/admin/members/invited')}
                                    onClick={() => navigate('/admin/members/invited')}
                                >
                                    <ListItemText primary={t('nav.menu.admin_invited_members', 'Invited Members')} />
                                </ListItemButton>
                            </ListItem>
                        </Collapse>
                    </>
                )}

                {isOrgAdmin && (
                    <>
                        <ListItem disablePadding>
                            <ListItemButton onClick={() => setReportingOpen(!reportingOpen)}>
                                <ListItemIcon>
                                    <AssessmentIcon />
                                </ListItemIcon>
                                <ListItemText>{t('nav.menu.reports', 'Reports')}</ListItemText>
                                {coursesOpen ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                        </ListItem>
                        <Collapse in={reportingOpen} timeout="auto" unmountOnExit>
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={isActive('/reports/registered-users')}
                                    onClick={() => navigate('/reports/registered-users')}
                                >
                                    <ListItemText
                                        primary={t('nav.menu.reports_registered_users', 'Registered Users')}
                                    />
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={isActive('/reports/course-completions')}
                                    onClick={() => navigate('/reports/course-completions')}
                                >
                                    <ListItemText
                                        primary={t('nav.menu.reports_course_completions', 'Course Completions')}
                                    />
                                </ListItemButton>
                            </ListItem>
                        </Collapse>
                    </>
                )}

                {isSuperuser && (
                    <>
                        <ListItem disablePadding>
                            <ListItemButton onClick={() => setSiteAdminOpen(!siteAdminOpen)}>
                                <ListItemIcon>
                                    <AdminPanelSettings />
                                </ListItemIcon>
                                <ListItemText>{t('nav.menu.platform_administration', 'Platform Admin')}</ListItemText>
                                {coursesOpen ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                        </ListItem>
                        <Collapse in={siteAdminOpen} timeout="auto" unmountOnExit>
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={isActive('/platform-admin/orgs')}
                                    onClick={() => navigate('/platform-admin/orgs')}
                                >
                                    <ListItemText primary={t('nav.menu.platform_admin_orgs', 'Organizations')} />
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={isActive('/platform-admin/users')}
                                    onClick={() => navigate('/platform-admin/users')}
                                >
                                    <ListItemText primary={t('nav.menu.platform_admin_users', 'Users')} />
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={isActive('/platform-admin/courses')}
                                    onClick={() => navigate('/platform-admin/courses')}
                                >
                                    <ListItemText primary={t('nav.menu.platform_admin_create_course', 'Courses')} />
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={isActive('/platform-admin/videos')}
                                    onClick={() => navigate('/platform-admin/videos')}
                                >
                                    <ListItemText primary={t('nav.menu.platform_admin_videos', 'Videos')} />
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={isActive('/platform-admin/emails')}
                                    onClick={() => navigate('/platform-admin/emails')}
                                >
                                    <ListItemText primary={t('nav.menu.platform_admin_emails', 'Emails')} />
                                </ListItemButton>
                            </ListItem>
                        </Collapse>
                    </>
                )}
            </List>
        </Box>
    );
};
